import { useMemo, useState } from "react";
import * as Analytics from "~/modules/media_citations/analytics";
import { MediaCitation } from "../../types";
import { CitationCard } from "./CitationCard";
import { CitationListRow } from "./CitationListRow";
import {
    ALL_OTHER_PUBLISHERS,
    CitationsFilterSidebar,
    getSmallPublisherIds,
} from "./CitationsFilterSidebar";
import { CitationsSectionHeader } from "./CitationsSectionHeader";
import { CitationSort, CitationTypeFilter } from "./types";
import { CitationsListEmptyState } from "./CitationsListEmptyState";

// // // //

// The featured card grid shows only the most recent pins; older featured
// citations still appear in the ledger below
const FEATURED_LIMIT = 20;

// Rows past this point lazy-load their images as the user scrolls
const EAGER_IMAGE_ROW_COUNT = 15;

function byNewest(a: MediaCitation, b: MediaCitation): number {
    return (
        new Date(b.published_date).getTime() -
        new Date(a.published_date).getTime()
    );
}

function matchesType(
    citation: MediaCitation,
    typeFilter: CitationTypeFilter,
): boolean {
    if (typeFilter === "institutional") {
        return citation.is_pdf;
    }

    if (typeFilter === "media") {
        return !citation.is_pdf;
    }

    return true;
}

function matchesPublisher(
    citation: MediaCitation,
    publisherFilter: string | null,
    smallPublisherIds: Set<string>,
): boolean {
    if (publisherFilter === null) {
        return true;
    }

    if (publisherFilter === ALL_OTHER_PUBLISHERS) {
        return smallPublisherIds.has(citation.publisher_id);
    }

    return citation.publisher_id === publisherFilter;
}

function matchesSearch(citation: MediaCitation, query: string): boolean {
    if (query === "") {
        return true;
    }

    return (
        citation.title.toLowerCase().includes(query) ||
        citation.publisher.name.toLowerCase().includes(query)
    );
}

// // // //

export function MediaCitationsListPage(props: { citations: MediaCitation[] }) {
    const { citations } = props;

    const [typeFilter, setTypeFilter] = useState<CitationTypeFilter>("all");
    const [publisherFilter, setPublisherFilter] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState<CitationSort>("newest");

    const featuredCitations = useMemo(
        () =>
            citations
                .filter((citation) => citation.featured)
                .sort(byNewest)
                .slice(0, FEATURED_LIMIT),
        [citations],
    );

    // Publishers merged into the sidebar's "All Others" facet
    const smallPublisherIds = useMemo(
        () => getSmallPublisherIds(citations),
        [citations],
    );

    const query = search.trim().toLowerCase();

    // Publisher facet counts reflect the active type filter + search query
    const publisherFacetCitations = useMemo(
        () =>
            citations.filter(
                (citation) =>
                    matchesType(citation, typeFilter) &&
                    matchesSearch(citation, query),
            ),
        [citations, typeFilter, query],
    );

    const filteredCitations = useMemo(() => {
        const filtered = citations.filter(
            (citation) =>
                matchesType(citation, typeFilter) &&
                matchesPublisher(
                    citation,
                    publisherFilter,
                    smallPublisherIds,
                ) &&
                matchesSearch(citation, query),
        );

        if (sort === "oldest") {
            return filtered.sort((a, b) => byNewest(b, a));
        }

        if (sort === "publisher") {
            return filtered.sort(
                (a, b) =>
                    a.publisher.name.localeCompare(b.publisher.name) ||
                    byNewest(a, b),
            );
        }

        return filtered.sort(byNewest);
    }, [
        citations,
        typeFilter,
        publisherFilter,
        smallPublisherIds,
        query,
        sort,
    ]);

    function clearFilters(source: "header" | "empty-state") {
        Analytics.trackClearFilters({ source });

        setTypeFilter("all");
        setPublisherFilter(null);
        setSearch("");
    }

    const hasActiveFilters =
        typeFilter !== "all" || publisherFilter !== null || search !== "";

    return (
        <>
            {/* <CitationsCtaBanner citations={citations} /> */}

            {featuredCitations.length > 0 && (
                <div className="mb-9">
                    <h2 className="mb-3 text-2xl font-light text-foreground">
                        Featured Publications
                    </h2>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {featuredCitations.map((citation) => (
                            <CitationCard
                                key={citation.id}
                                citation={citation}
                            />
                        ))}
                    </div>
                </div>
            )}

            <CitationsSectionHeader
                hasActiveFilters={hasActiveFilters}
                search={search}
                typeFilter={typeFilter}
                sort={sort}
                onClearFilters={() => clearFilters("header")}
                onSearchChange={(nextValue) => {
                    setSearch(nextValue);

                    // A new search invalidates the publisher selection
                    if (nextValue !== "") {
                        setPublisherFilter(null);
                    }
                }}
                onTypeFilterChange={(nextValue) => {
                    Analytics.trackChangeTypeFilter({
                        prevValue: typeFilter,
                        nextValue,
                    });

                    setTypeFilter(nextValue);
                    setPublisherFilter(null);
                }}
                onSortChange={(nextValue) => {
                    Analytics.trackChangeSort({
                        prevValue: sort,
                        nextValue,
                    });

                    setSort(nextValue);
                }}
            />

            {filteredCitations.length === 0 && (
                <CitationsListEmptyState
                    onClearFilters={() => clearFilters("empty-state")}
                />
            )}

            {filteredCitations.length > 0 && (
                <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[248px_minmax(0,1fr)]">
                    <CitationsFilterSidebar
                        citations={citations}
                        publisherFacetCitations={publisherFacetCitations}
                        publisherFilter={publisherFilter}
                        onPublisherFilterChange={setPublisherFilter}
                    />

                    <div className="min-w-0 overflow-hidden rounded-xl border bg-card shadow-2xs">
                        {filteredCitations.map((citation, index) => (
                            <CitationListRow
                                key={citation.id}
                                citation={citation}
                                imageLoading={
                                    index < EAGER_IMAGE_ROW_COUNT
                                        ? "eager"
                                        : "lazy"
                                }
                            />
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}
